import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, List, X, ArrowClockwise, SunFill, MoonFill,
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { useLocale } from '../i18n/LocaleContext';
import './PortalShell.scss';

// A gentle amber "!" next to a nav item that needs attention (billing not set
// up, the fit-check embed not connected). A nudge, not a block; purely visual,
// so it carries a title for the hover and is aria-hidden.
function NavBadge({ show, title }) {
  if (!show) return null;
  return (
    <span className="portal-nav-badge" title={title} aria-hidden="true" data-testid="nav-badge">!</span>
  );
}

// Dashboard shell: the admin panel's sidebar (admin/src/pages/admin/AdminLayout.js)
// — so the three web apps share one navigation.
//
//   groups    → the admin's grouped menu, every page its own route:
//               [{ key, label, Icon, items: [{ key, label, Icon, to, sections? }] }]
//               or a top-level page [{ key, label, Icon, to }]
//   active    → key of the current item
//   onRefresh → async; re-fetches the current page's data in place
//   theme     → what usePortalTheme() returns; the shell owns only the button
//   footerTop → optional block at the top of the footer (brand switcher)
//   actions   → extra footer buttons after the admin's own
//               [{ key, label, Icon, onClick, danger? }]
//
// Like the admin, the rail collapses to icons on desktop, and on phones the
// whole sidebar becomes a top bar + overlay menu.

const COLLAPSED_KEY = 'partners_sidebar_collapsed';
const PANEL_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 768px)';

function usePanelQuery() {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(PANEL_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia?.(PANEL_QUERY);
    if (!query) return undefined;
    const update = () => setMatches(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return matches;
}

const isCurrent = (item, active) => item.key === active || !!item.sections?.some((section) => section.key === active);

function panelPosition(sidebar) {
  const side = sidebar.getBoundingClientRect();
  return {
    top: side.top,
    left: side.left,
    height: side.height,
    '--panel-offset': `${side.width}px`,
  };
}

function GroupPanel({ panel, active, onEnter, onLeave, onPick }) {
  const [position, setPosition] = useState(null);
  useLayoutEffect(() => {
    if (panel) setPosition(panelPosition(panel.sidebar));
  }, [panel]);
  if (!panel || !position) return null;
  const { group } = panel;
  return (
    <div
      key={group.key}
      className="portal-panel"
      style={position}
      role="menu"
      aria-label={group.label}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="portal-panel-head">{group.label}</div>
      <div className="portal-panel-list">
        {group.items.map((item) => (
          <div key={item.key} className="portal-panel-entry">
            <Link
              to={item.to}
              role="menuitem"
              onClick={onPick}
              aria-current={active === item.key ? 'page' : undefined}
              className={`portal-panel-item${isCurrent(item, active) ? ' is-current' : ''}`}
            >
              <item.Icon className="portal-panel-icon" />
              <span>{item.label}</span>
              <NavBadge show={item.badge === 'warn'} title={item.badgeTitle} />
            </Link>
            {item.sections?.length > 0 && (
              <div className="portal-panel-sections">
                {item.sections.map((section) => (
                  <Link
                    key={section.key}
                    to={section.to}
                    role="menuitem"
                    onClick={onPick}
                    aria-current={active === section.key ? 'page' : undefined}
                    className={`portal-panel-section${active === section.key ? ' is-current' : ''}`}
                  >
                    <section.Icon className="portal-panel-section-icon" />
                    <span>{section.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
const THEME_KEY = 'portal_theme';

// Round flag SVGs for the language switcher — the same ones the admin uses.
const FLAG_SRC = {
  hu: 'https://globalartinc.github.io/round-flags/flags/hu.svg',
  en: 'https://globalartinc.github.io/round-flags/flags/en.svg',
};

// localStorage can throw inside the Shopify admin iframe (third-party storage
// blocked) — the setting then just isn't remembered.
function readSetting(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSetting(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* not persisted — see readSetting */
  }
}

// Light / dark theme for the dashboard. Dark is a class on <html> rather than
// on the shell, so what is portalled to <body> (the brand menu, modals) and
// the loading screens before the shell mounts are themed too. Removed on
// unmount: the marketing pages and login stay light.
//
// Light by default — the portals have always been light; the choice is then
// remembered, like the admin's.
export function usePortalTheme() {
  const [isDark, setIsDark] = useState(() => readSetting(THEME_KEY) === 'dark');

  // Layout effect, so the first dark frame is never painted light.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('portal-dark', isDark);
    return () => root.classList.remove('portal-dark');
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      writeSetting(THEME_KEY, prev ? 'light' : 'dark');
      return !prev;
    });
  }, []);

  return { isDark, toggleTheme };
}

export default function PortalShell({ groups, active, onRefresh, theme, footerTop, actions = [], children }) {
  const { lang, setLang, t } = useLocale();
  const [collapsed, setCollapsed] = useState(() => readSetting(COLLAPSED_KEY) === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { isDark, toggleTheme } = theme;
  const [panel, setPanel] = useState(null);
  const panelQuery = usePanelQuery();
  const panelTimer = useRef(null);
  const panelMode = panelQuery && !collapsed;
  const activeGroupKey = (groups.find((g) => g.items?.some((i) => isCurrent(i, active))) || groups.find((g) => g.items))?.key;
  const [openGroup, setOpenGroup] = useState(activeGroupKey);

  useEffect(() => { setOpenGroup(activeGroupKey); }, [activeGroupKey]);
  useEffect(() => () => clearTimeout(panelTimer.current), []);
  useEffect(() => {
    if (!panelMode) setPanel(null);
  }, [panelMode]);
  useEffect(() => {
    if (!panel) return undefined;
    const close = () => setPanel(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [panel]);

  function openPanel(group, element) {
    clearTimeout(panelTimer.current);
    if (!panelMode) return;
    const sidebar = element.closest('.portal-sidebar');
    setPanel((prev) => (prev?.group.key === group.key ? prev : { group, sidebar }));
  }

  function togglePanel(group, element) {
    if (panel?.group.key === group.key) setPanel(null);
    else openPanel(group, element);
  }

  function schedulePanelClose() {
    clearTimeout(panelTimer.current);
    panelTimer.current = setTimeout(() => setPanel(null), 180);
  }

  function toggleGroup(key) {
    setOpenGroup((prev) => (prev === key ? null : key));
  }

  // Freeze the page behind the mobile menu overlay.
  useEffect(() => {
    document.body.classList.toggle('portal-menu-open', mobileOpen);
    return () => document.body.classList.remove('portal-menu-open');
  }, [mobileOpen]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    writeSetting(COLLAPSED_KEY, next ? '1' : '0');
  }

  // Refresh re-fetches in place instead of reloading the browser: the page,
  // the brand and anything scrolled stay put, and nothing flashes blank. The
  // icon spins for at least a beat, so a fast answer still reads as "done".
  async function handleRefresh() {
    if (refreshing) return;
    setMobileOpen(false);
    setRefreshing(true);
    try {
      await Promise.all([onRefresh(), new Promise((resolve) => setTimeout(resolve, 600))]);
    } catch {
      /* the page shows its own error state */
    } finally {
      setRefreshing(false);
    }
  }

  const refreshIcon = <ArrowClockwise className={refreshing ? 'portal-refresh-spin' : undefined} />;

  return (
    <div className={`portal-shell${collapsed ? ' portal-shell--collapsed' : ''}`}>
      <aside
        className={`portal-sidebar${collapsed ? ' portal-sidebar--collapsed' : ''}${
          mobileOpen ? ' portal-sidebar--mobile-open' : ''
        }${panel ? ' portal-sidebar--expanded' : ''}`}
      >
        <div className="portal-sidebar-head">
          <div className="portal-sidebar-logo">
            Outfino
            <span className="portal-sidebar-logo-sub">Partners</span>
          </div>
          <button
            type="button"
            className="portal-sidebar-toggle"
            onClick={toggleCollapsed}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </button>
          <button
            type="button"
            className="portal-mobile-toggle"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? t('sidebar.menuClose') : t('sidebar.menu')}
          >
            {mobileOpen ? <X /> : <List />}
          </button>
          <button
            type="button"
            className="portal-mobile-refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label={t('shell.refresh')}
          >
            {refreshIcon}
          </button>
        </div>

        <nav className="portal-nav">
          {groups.map((group) => {
            if (!group.items) {
              return (
                <div key={group.key} className={`portal-nav-group portal-nav-group--link${panelMode ? ' portal-nav-group--panel' : ''}`}>
                  <Link
                    to={group.to}
                    title={group.label}
                    onClick={() => setMobileOpen(false)}
                    onMouseEnter={() => panel && setPanel(null)}
                    aria-current={active === group.key ? 'page' : undefined}
                    className={`portal-nav-group-head portal-nav-group-head--link${active === group.key ? ' portal-nav-group-head--current' : ''}`}
                  >
                    <group.Icon className="portal-nav-group-icon" />
                    <span className="portal-nav-group-label">{group.label}</span>
                    <NavBadge show={group.badge === 'warn'} title={group.badgeTitle} />
                  </Link>
                </div>
              );
            }
            const groupClosed = panelMode || group.key !== openGroup;
            const groupCurrent = group.items.some((i) => isCurrent(i, active));
            return (
              <div
                key={group.key}
                className={`portal-nav-group${groupClosed ? ' portal-nav-group--closed' : ''}${panelMode ? ' portal-nav-group--panel' : ''}`}
              >
                <button
                  type="button"
                  className={`portal-nav-group-head${panel?.group.key === group.key ? ' portal-nav-group-head--hover' : ''}${panelMode && groupCurrent ? ' portal-nav-group-head--current' : ''}`}
                  onClick={(e) => (panelMode ? togglePanel(group, e.currentTarget) : toggleGroup(group.key))}
                  onMouseEnter={(e) => openPanel(group, e.currentTarget)}
                  onMouseLeave={schedulePanelClose}
                  onFocus={(e) => openPanel(group, e.currentTarget)}
                  aria-expanded={panelMode ? panel?.group.key === group.key : !groupClosed}
                  aria-haspopup={panelMode ? 'menu' : undefined}
                  title={panelMode ? undefined : group.label}
                >
                  {panelMode && group.Icon && <group.Icon className="portal-nav-group-icon" />}
                  <span className="portal-nav-group-label">{group.label}</span>
                  {panelMode ? <ChevronRight className="portal-nav-group-chevron" /> : <ChevronDown className="portal-nav-group-chevron" />}
                </button>

                {!panelMode && (
                  <div className="portal-nav-group-items">
                    <div className="portal-nav-group-items-inner">
                      {group.items.map((item) => (
                        <React.Fragment key={item.key}>
                          <Link
                            to={item.to}
                            title={item.label}
                            onClick={() => setMobileOpen(false)}
                            aria-current={active === item.key ? 'page' : undefined}
                            className={`portal-nav-item${active === item.key ? ' portal-nav-item--active' : ''}`}
                          >
                            <item.Icon className="portal-nav-icon" />
                            <span className="portal-nav-label">{item.label}</span>
                            <NavBadge show={item.badge === 'warn'} title={item.badgeTitle} />
                          </Link>
                          {item.sections?.map((section) => (
                            <Link
                              key={section.key}
                              to={section.to}
                              title={section.label}
                              onClick={() => setMobileOpen(false)}
                              aria-current={active === section.key ? 'page' : undefined}
                              className={`portal-nav-item portal-nav-item--section${active === section.key ? ' portal-nav-item--active' : ''}`}
                            >
                              <section.Icon className="portal-nav-icon" />
                              <span className="portal-nav-label">{section.label}</span>
                            </Link>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="portal-sidebar-footer">
          {footerTop}
          <button
            type="button"
            className="portal-sidebar-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title={t('shell.refresh')}
          >
            {refreshIcon}
            <span className="portal-nav-label">{t('shell.refresh')}</span>
          </button>
          <button
            type="button"
            className="portal-sidebar-btn"
            onClick={() => setLang(lang === 'hu' ? 'en' : 'hu')}
            title={t('lang.switchTitle')}
          >
            <img
              className="portal-lang-flag"
              src={FLAG_SRC[lang]}
              alt={lang === 'hu' ? 'Magyar' : 'English'}
              width={18}
              height={18}
            />
            <span className="portal-nav-label">{t('lang.name')}</span>
          </button>
          <button
            type="button"
            className="portal-sidebar-btn"
            onClick={toggleTheme}
            title={isDark ? t('theme.lightTitle') : t('theme.darkTitle')}
          >
            {isDark ? <SunFill /> : <MoonFill />}
            <span className="portal-nav-label">{isDark ? t('theme.light') : t('theme.dark')}</span>
          </button>
          {actions.map(({ key, label, Icon, onClick, danger }) => (
            <button
              key={key}
              type="button"
              className={`portal-sidebar-btn${danger ? ' portal-sidebar-btn--danger' : ''}`}
              onClick={() => {
                setMobileOpen(false);
                onClick();
              }}
              title={label}
            >
              <Icon />
              <span className="portal-nav-label">{label}</span>
            </button>
          ))}
        </div>
      </aside>
      <GroupPanel
        panel={panel}
        active={active}
        onEnter={() => clearTimeout(panelTimer.current)}
        onLeave={schedulePanelClose}
        onPick={() => setPanel(null)}
      />

      <main className="portal-main">{children}</main>
    </div>
  );
}
